import React, { useState } from "react";
import PropTypes from "prop-types";
import { Grid, Typography } from "@material-ui/core";
import Modal from "../../../app/components/modals/Modal";
import useStyles from "./styles";
import lifeInsuranceImage from "../../../assets/img/life-insurance.png";
import { translate as t } from "../../translate/localization";
import CobeeButton from "../../../app/components/CobeeButton";
import ModalityConfig from "./components/ModalityConfig";
import { MonetaryAmount } from "../../../app/domain/monetary-amount";

export default function LifeInsuranceAdvancedConfigModal({
  benefitModel,
  config,
  callback,
}) {
  const { container, benefitImage, tooltipContainer, blocksContainer } =
    useStyles();

  const [isLoading, setIsLoading] = useState(false);

  const [configsPayload, setConfigsPayload] = useState(
    config.map((modality) => ({
      ...modality,
      capitalConfigs: modality.capitalConfigs.map((capital) => ({
        ...capital,
        allowanceType: "monthly-fee-ratio",
      })),
    }))
  );

  const getCapitalTypeStructure = ({ type, value }) => {
    const capitalTypes = {
      fixed: {
        allowanceAmountRatio: 0,
        allowanceFixedAmount: MonetaryAmount.fromUnitary(value),
      },
      "monthly-fee-ratio": {
        allowanceAmountRatio: value,
        allowanceFixedAmount: MonetaryAmount.fromUnitary(0),
      },
    };
    return capitalTypes[type];
  };

  const handleModalityChange = ({ modalityId, value }) => {
    setConfigsPayload(
      configsPayload.map(({ modalityId: id, capitalConfigs, ...rest }) => {
        if (id === modalityId) {
          const allowanceType = "monthly-fee-ratio";
          return {
            capitalConfigs: capitalConfigs.map((capital, index) => ({
              ...capital,
              ...getCapitalTypeStructure({
                type: allowanceType,
                value: index < value ? 1 : 0,
              }),
              allowanceType,
            })),
            modalityId: id,
            ...rest,
          };
        }
        return { modalityId: id, capitalConfigs, ...rest };
      })
    );
  };

  const handleModalityToggle = ({ modalityId }) => {
    setConfigsPayload(
      configsPayload.map(({ modalityId: id, enabled, ...rest }) => ({
        enabled: id === modalityId ? !enabled : enabled,
        modalityId: id,
        ...rest,
      }))
    );
  };

  return (
    <Modal>
      <Grid item xs={12} container justify="center" className={container}>
        <Grid item xs={2} container justify="center" alignItems="center">
          <img
            src={lifeInsuranceImage}
            alt="life-insurance"
            className={benefitImage}
          />
        </Grid>
        <Grid item xs={10} container alignItems="center" justify="flex-start">
          <Typography variant="h4">
            {t("benefits-section.life-insurance-benefit.life-insurance")}
          </Typography>
        </Grid>
        <Grid item xs={12} container className={tooltipContainer}>
          <Typography variant="body2">
            {t("benefits-section.life-insurance-benefit.benefit-model-tooltip")}
          </Typography>
        </Grid>
        <Grid item xs={12} container className={tooltipContainer}>
          <Typography variant="h5">
            {t("common-messages.adjustments")}
          </Typography>
        </Grid>
        <Grid item xs={12} container className={tooltipContainer}>
          <Typography variant="body1">
            {t(
              "benefits-section.life-insurance-benefit.advanced-config-tooltip"
            )}
            :
          </Typography>
        </Grid>
        <Grid item container xs={12} className={blocksContainer}>
          {configsPayload.map(({ modalityId, capitalConfigs, ...rest }) => (
            <ModalityConfig
              key={modalityId}
              modalityId={modalityId}
              onChange={handleModalityChange}
              onToggle={() => handleModalityToggle({ modalityId })}
              capitalConfigs={capitalConfigs}
              defaultSlideValue={capitalConfigs.reduce(
                (acc, { allowanceAmountRatio }) => acc + allowanceAmountRatio,
                0
              )}
              {...rest}
            />
          ))}
        </Grid>
        <Grid item container xs={12} justify="flex-end">
          <CobeeButton
            isLoading={isLoading}
            disabled={isLoading}
            onClick={async () => {
              setIsLoading(true);
              await callback({
                ...benefitModel,
                lifeInsuranceConfigs: configsPayload,
              });
            }}
          >
            {t("actions.save")}
          </CobeeButton>
        </Grid>
      </Grid>
    </Modal>
  );
}

LifeInsuranceAdvancedConfigModal.defaultProps = {};

LifeInsuranceAdvancedConfigModal.propTypes = {
  config: PropTypes.array.isRequired,
  benefitModel: PropTypes.object.isRequired,
  callback: PropTypes.func.isRequired,
};
